/**
 * smartTitleCase.js
 * Converts ALL-CAPS words to Title Case while preserving acronyms.
 * Mixed-case and lowercase words are left untouched.
 *
 * Acronym detection:
 *  1. Known acronym set (case-insensitive lookup)
 *  2. No-vowel heuristic – all-caps words with zero vowels (A E I O U Y)
 *     are almost certainly abbreviations (e.g. IBM, NFT, CNN, SMTP).
 *  3. Single-character words are never lowered (e.g. "I", "A").
 */

const KNOWN_ACRONYMS = new Set([
  "AI", "AML", "API", "APR", "APY", "AR",
  "B2B", "B2C", "BBC", "BTC",
  "CBD", "CEO", "CEX", "CFO", "CIA", "CNN", "COO", "CSS", "CTO",
  "DAO", "DAPP", "DEX", "DIY", "DNA",
  "ERP", "ESG", "ETF", "ETH", "EU", "EUR",
  "FAQ", "FBI", "FIFA", "FYI",
  "GBP", "GDP", "GIF", "GMT", "GPS",
  "HBO", "HD", "HTML", "HTTP", "HTTPS",
  "IBM", "ICO", "ID", "IOT", "IPO",
  "JPEG", "JPG", "JS", "JSON",
  "KYC",
  "LCD", "LED", "LLC", "LP", "LTD",
  "MBA", "MIT", "MMA", "MRI", "MVP",
  "NASA", "NATO", "NBA", "NBC", "NFL", "NFT", "NGO", "NHL", "NYSE",
  "OK",
  "P2P", "PC", "PDF", "PHD", "PIN", "PM", "PR",
  "QR",
  "RAM", "ROI", "RSVP",
  "SAAS", "SAT", "SEC", "SEO", "SIM", "SMS", "SQL", "SUV", "SVG",
  "TLDR", "TS", "TV", "TVL",
  "UAE", "UCLA", "UFO", "UK", "UN", "UNICEF", "UPS", "URI", "URL",
  "US", "USA", "USB", "USD", "UI", "UX",
  "VAR", "VAT", "VFX", "VIP", "VPN", "VR", "VS",
  "WHO", "WIFI",
  "XR", "XML",
  "YMCA",
  "ZIP",
]);

/**
 * @param {string} word – an all-caps letter sequence
 * @returns {boolean}
 */
function isLikelyAcronym(word) {
  if (word.length <= 1) return true;
  if (KNOWN_ACRONYMS.has(word.toUpperCase())) return true;
  // No standard vowels (A E I O U Y) – almost certainly an abbreviation
  if (!/[AEIOUY]/i.test(word)) return true;
  return false;
}

/**
 * Convert ALL-CAPS words in text to Title Case, preserving acronyms.
 * Words that are already mixed-case or lowercase pass through unchanged.
 *
 * @param {string} text
 * @returns {string}
 */
export function smartTitleCase(text) {
  if (!text || typeof text !== "string") return text || "";

  // Match every consecutive-letter run independently.
  // This naturally handles hyphens (WELL-KNOWN → Well-Known),
  // slashes, digits adjacent to letters, etc.
  return text.replace(/[A-Za-z]+/g, (word) => {
    // Only touch words that are entirely uppercase and at least 2 chars
    if (word.length < 2 || word !== word.toUpperCase()) return word;

    if (isLikelyAcronym(word)) return word;

    // Title Case: first letter stays upper, rest lower
    return word[0] + word.slice(1).toLowerCase();
  });
}
